import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { X, CalendarDays, Clock, Users, AlertTriangle, ImageIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  commonAreaReservationService,
  CommonArea,
  CommonAreaResource,
} from '../../services/commonAreaReservationService';
import { PersonAddressPicker } from '../people/PersonAddressPicker';
import ReservaCalendar from './ReservaCalendar';
import { useAreaCalendarData } from './useAreaCalendarData';
import { CondoType } from '../../utils/terminology';
import {
  areaBookingMode,
  busyIntervalsFor,
  localRangeFor,
  occupiedDatesFrom,
  operatingWindowLabel,
  suggestedStartTimes,
  validateReservation,
} from './reservaRules';
import { areaPhotos } from './areaPhotos';

/**
 * "Nova Reserva", as the portaria uses it.
 *
 * Built like the person modal — the space on the left (photos first: that is how an operator
 * recognises which área a resident is asking about over the counter), the form on the right — and
 * it answers the questions the old one could not:
 *
 *   - an área booked BY THE DAY asks for no times at all, and prints the hours the space actually
 *     works (never "dia inteiro", which residents read as 24 hours);
 *   - an área booked by the hour only offers the hours that are still free, with what is already
 *     taken listed beside them;
 *   - nothing is sent that the rules already refuse, so the operator is told before they type a
 *     full form, not after a 409.
 *
 * Every rule lives in reservaRules.ts, tested there and shared with whatever else needs it. This
 * file is the screen.
 */

export interface NovaReservaModalProps {
  customerId: string;
  areas: CommonArea[];
  /** Operational view runs light or dark; the normal page is always light. */
  lightTheme?: boolean;
  condoType?: CondoType;
  /** 'condominio' | 'empresa' — morador by unidade vs funcionário by departamento. */
  customerType?: string;
  /** Pre-selects an área when the operator opened this from one. */
  initialAreaId?: string;
  /**
   * Whether a person must be named. True for staff booking on a resident's behalf (the only way
   * this modal is used today); a resident booking for themselves would pass false.
   */
  canBookOnBehalf?: boolean;
  /** Injected by tests so "hoje" is deterministic. */
  now?: Date;
  onClose: () => void;
  onSuccess: () => void;
}

interface PickedPerson {
  id: string;
  first_name: string;
  last_name: string;
}

const cn = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(' ');

/** yyyy-mm-dd for a Date, read from its LOCAL parts (toISOString would shift the day). */
const localDateString = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** dd/mm/aaaa for a yyyy-mm-dd, read lexically so no timezone can move the day. */
const formatBR = (dateISO: string): string =>
  `${dateISO.slice(8, 10)}/${dateISO.slice(5, 7)}/${dateISO.slice(0, 4)}`;

const NovaReservaModal: React.FC<NovaReservaModalProps> = ({
  customerId,
  areas,
  lightTheme = true,
  condoType,
  customerType,
  initialAreaId,
  canBookOnBehalf = true,
  now,
  onClose,
  onSuccess,
}) => {
  const today = useMemo(() => now ?? new Date(), [now]);

  // `!== false`: an absent flag is a field the response did not include, not a deactivated área.
  // Filtering on truthiness emptied the picker entirely — see ReservationModal.test.tsx.
  const activeAreas = useMemo(() => areas.filter(a => a.is_active !== false), [areas]);
  const [areaId, setAreaId] = useState(initialAreaId || (activeAreas.length === 1 ? activeAreas[0].id : ''));
  const [person, setPerson] = useState<PickedPerson | null>(null);
  const [dateISO, setDateISO] = useState('');
  const [startTime, setStartTime] = useState('');
  const [durationHours, setDurationHours] = useState(1);
  const [purpose, setPurpose] = useState('');
  const [notes, setNotes] = useState('');
  const [resourceId, setResourceId] = useState('');
  const [resources, setResources] = useState<CommonAreaResource[]>([]);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const { bookings, blackouts } = useAreaCalendarData(areaId, today);

  const area = useMemo(() => activeAreas.find(a => a.id === areaId) ?? null, [activeAreas, areaId]);
  const mode = area ? areaBookingMode(area) : 'whole_day';
  const photos = useMemo(() => areaPhotos(area), [area]);
  const maxDuration = Math.max(1, area?.max_duration_hours || 1);

  // What the space is doing on the chosen date — the window drives both the label and the hours
  // on offer, so an área with a different Saturday is handled without a special case here.
  const windowLabel = area ? operatingWindowLabel(area, dateISO || localDateString(today)) : '';
  const busy = useMemo(
    () => (area && dateISO ? busyIntervalsFor(bookings, dateISO) : []),
    [area, dateISO, bookings],
  );
  const startOptions = useMemo(
    () => (area && dateISO && mode === 'time_slot' ? suggestedStartTimes(area, dateISO, bookings, durationHours) : []),
    [area, dateISO, bookings, durationHours, mode],
  );
  const occupiedDates = useMemo(() => occupiedDatesFrom(bookings), [bookings]);
  const dayIsTaken = mode === 'whole_day' && !!dateISO && occupiedDates.has(dateISO);

  const endTime = useMemo(() => {
    if (mode !== 'time_slot' || !startTime) return '';
    const [h, m] = startTime.split(':').map(Number);
    const total = h * 60 + m + durationHours * 60;
    return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
  }, [mode, startTime, durationHours]);

  // Area changed: its resources and its photos belong to the new one (its calendar comes from
  // the shared hook, which refetches on the same change).
  useEffect(() => {
    setResourceId('');
    setPhotoIndex(0);
    setStartTime('');
    if (!areaId) {
      setResources([]);
      return;
    }
    let cancelled = false;
    commonAreaReservationService.getResources(areaId)
      .then(list => { if (!cancelled) setResources(list.filter(r => r.is_active)); })
      .catch(() => { if (!cancelled) setResources([]); });
    return () => { cancelled = true; };
  }, [areaId]);

  // A start time that stopped being available (duration changed, or the day did) must not stay
  // selected — it would submit an hour the list no longer offers.
  useEffect(() => {
    if (startTime && !startOptions.includes(startTime)) setStartTime('');
  }, [startOptions, startTime]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!area) { toast.error('Selecione uma área'); return; }
    if (canBookOnBehalf && !person) { toast.error('Selecione o morador para quem está reservando'); return; }
    if (resources.length > 0 && !resourceId) { toast.error('Selecione um recurso'); return; }

    const problem = validateReservation({
      area,
      dateISO,
      start: startTime,
      end: endTime,
      bookings,
      blackoutDates: blackouts,
      now: today,
    });
    if (problem) { toast.error(problem); return; }

    const range = localRangeFor({ area, dateISO, start: startTime, end: endTime });

    setSubmitting(true);
    try {
      const booking = await commonAreaReservationService.createBooking(area.id, {
        customer_id: customerId,
        person_id: person?.id,
        resource_id: resourceId || undefined,
        start_time: range.start_time,
        end_time: range.end_time,
        purpose: purpose.trim(),
        notes: notes.trim() || undefined,
      });
      toast.success(booking.status === 'Pending' ? 'Reserva enviada para aprovação!' : 'Reserva criada!');
      if (booking.guest_list_token && area.capacity > 1) {
        const link = `${window.location.origin}/convidados/${booking.guest_list_token}`;
        navigator.clipboard?.writeText(link)
          .then(() => toast.success('Link para lista de convidados copiado!'))
          .catch(() => {});
      }
      onSuccess();
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao criar reserva');
    } finally {
      setSubmitting(false);
    }
  }, [area, canBookOnBehalf, person, purpose, resources.length, resourceId, dateISO, startTime, endTime,
      bookings, blackouts, today, customerId, notes, onSuccess]);

  const text = lightTheme ? 'text-gray-900' : 'text-white';
  const subtext = lightTheme ? 'text-gray-500' : 'text-gray-400';
  const divider = lightTheme ? 'border-gray-200' : 'border-gray-700';
  const panel = lightTheme ? 'bg-white border-gray-200' : 'bg-gray-900 border-gray-700';
  const sidePanel = lightTheme ? 'bg-gray-50 border-gray-200' : 'bg-gray-950 border-gray-800';
  const label = cn('block text-xs font-semibold mb-1', lightTheme ? 'text-gray-700' : 'text-gray-300');
  const input = cn(
    'w-full px-3 py-2 text-sm rounded-lg border focus:outline-none focus:ring-1',
    lightTheme
      ? 'bg-white border-gray-300 text-gray-800 placeholder-gray-400 focus:border-blue-400 focus:ring-blue-200'
      : 'bg-gray-800 border-gray-700 text-gray-200 placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500/20',
  );

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className={cn('w-full max-w-4xl rounded-2xl shadow-2xl border flex flex-col max-h-[92vh]', panel)}>
        {/* Header */}
        <div className={cn('flex items-center justify-between px-6 py-4 border-b flex-shrink-0', divider)}>
          <h3 className={cn('text-base font-bold', text)}>Nova Reserva</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className={cn('p-1.5 rounded-lg transition-colors', lightTheme ? 'hover:bg-gray-100 text-gray-500' : 'hover:bg-gray-800 text-gray-400')}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="flex flex-col md:flex-row">
            {/* ── The space, on the left ─────────────────────────────────────── */}
            <aside className={cn('md:w-72 flex-shrink-0 border-b md:border-b-0 md:border-r p-4 space-y-3', sidePanel, divider)}>
              <div className={cn('aspect-[4/3] w-full rounded-xl overflow-hidden flex items-center justify-center border', divider,
                lightTheme ? 'bg-white' : 'bg-gray-900')}>
                {photos.length > 0 ? (
                  <img src={photos[Math.min(photoIndex, photos.length - 1)]} alt={area?.name || 'Área comum'} className="w-full h-full object-cover" />
                ) : (
                  <div className={cn('flex flex-col items-center gap-1', subtext)}>
                    <ImageIcon className="h-7 w-7" />
                    <span className="text-[11px]">Sem foto cadastrada</span>
                  </div>
                )}
              </div>

              {photos.length > 1 && (
                <div className="grid grid-cols-4 gap-1">
                  {photos.map((url, i) => (
                    <button
                      key={url}
                      type="button"
                      aria-label={`Foto ${i + 1}`}
                      onClick={() => setPhotoIndex(i)}
                      className={cn(
                        'aspect-[4/3] rounded border overflow-hidden',
                        i === photoIndex ? 'border-blue-500 ring-1 ring-blue-400' : divider,
                      )}
                    >
                      <img src={url} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}

              {area && (
                <div className="space-y-2">
                  <p className={cn('text-sm font-bold leading-tight', text)}>{area.name}</p>
                  {area.description && <p className={cn('text-xs', subtext)}>{area.description}</p>}

                  <div className={cn('flex items-center gap-1.5 text-xs', subtext)}>
                    <Clock className="h-3.5 w-3.5 flex-shrink-0" />
                    {/* The hours the space works — for a whole-day área this IS the booked range. */}
                    <span>{windowLabel}</span>
                  </div>
                  {area.capacity > 0 && (
                    <div className={cn('flex items-center gap-1.5 text-xs', subtext)}>
                      <Users className="h-3.5 w-3.5 flex-shrink-0" />
                      <span>Capacidade: {area.capacity}</span>
                    </div>
                  )}
                  {mode === 'time_slot' && area.max_duration_hours > 0 && (
                    <div className={cn('flex items-center gap-1.5 text-xs', subtext)}>
                      <CalendarDays className="h-3.5 w-3.5 flex-shrink-0" />
                      <span>Duração máxima: {area.max_duration_hours}h</span>
                    </div>
                  )}
                  {area.requires_approval && (
                    <p className={cn('flex items-center gap-1.5 text-xs font-medium', lightTheme ? 'text-gray-700' : 'text-gray-200')}>
                      <span className="h-1.5 w-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                      Esta área requer aprovação do síndico
                    </p>
                  )}
                  {(!!area.reservation_fee || !!area.late_cancellation_fee) && (
                    <p className={cn('text-xs', subtext)}>
                      {!!area.reservation_fee && <>Taxa: R$ {area.reservation_fee.toFixed(2)}</>}
                      {!!area.reservation_fee && !!area.late_cancellation_fee && ' · '}
                      {!!area.late_cancellation_fee && (
                        <>Multa por cancelamento tardio: R$ {area.late_cancellation_fee.toFixed(2)}</>
                      )}
                    </p>
                  )}
                  {area.rules && <p className={cn('text-xs whitespace-pre-line', subtext)}>{area.rules}</p>}
                </div>
              )}
            </aside>

            {/* ── The booking, on the right ──────────────────────────────────── */}
            <div className="flex-1 p-4 space-y-4 min-w-0">
              <div>
                <label htmlFor="reserva-area" className={label}>Área *</label>
                <select id="reserva-area" value={areaId} onChange={e => setAreaId(e.target.value)} className={input}>
                  <option value="">Selecione...</option>
                  {activeAreas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>

              {resources.length > 0 && (
                <div>
                  <label htmlFor="reserva-recurso" className={label}>Recurso *</label>
                  <select id="reserva-recurso" value={resourceId} onChange={e => setResourceId(e.target.value)} className={input}>
                    <option value="">Selecione...</option>
                    {resources.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                </div>
              )}

              {canBookOnBehalf && (
                <PersonAddressPicker
                  customerId={customerId}
                  customerType={customerType}
                  condoType={condoType}
                  value={person as any}
                  onChange={setPerson as any}
                />
              )}

              <div>
                <label className={label}>Data *</label>
                {/* The month, open — not a field that hides the calendar behind a button. The
                    operator has to SEE which days the área still has free, the way the resident
                    does in the app, before telling them anything at the counter. */}
                <ReservaCalendar
                  area={area}
                  bookings={bookings}
                  blackoutDates={blackouts}
                  value={dateISO}
                  onChange={setDateISO}
                  durationHours={durationHours}
                  now={today}
                  lightTheme={lightTheme}
                />
                {dateISO && (
                  <p className={cn('mt-1 text-xs', subtext)}>
                    Data escolhida: <span className={cn('font-semibold', text)}>{formatBR(dateISO)}</span>
                  </p>
                )}
              </div>

              {/* A whole-day área has nothing to type: it takes the space for the hours it works. */}
              {mode === 'whole_day' ? (
                dateISO && (
                  <div className={cn('rounded-lg border px-3 py-2 text-xs',
                    dayIsTaken
                      ? (lightTheme ? 'bg-gray-100 border-gray-200 text-gray-700' : 'bg-gray-800 border-gray-700 text-gray-200')
                      : (lightTheme ? 'bg-blue-50 border-blue-100 text-blue-800' : 'bg-blue-900/20 border-blue-900 text-blue-300'))}>
                    {dayIsTaken ? (
                      <span className="flex items-center gap-1.5">
                        <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
                        Este dia já possui uma reserva nesta área.
                      </span>
                    ) : (
                      <>A reserva vale das <span className="font-semibold">{windowLabel}</span>.</>
                    )}
                  </div>
                )
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="reserva-inicio" className={label}>Início *</label>
                    <select
                      id="reserva-inicio"
                      value={startTime}
                      onChange={e => setStartTime(e.target.value)}
                      disabled={!dateISO}
                      className={input}
                    >
                      <option value="">{dateISO ? 'Selecione...' : 'Escolha a data'}</option>
                      {startOptions.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="reserva-duracao" className={label}>Duração</label>
                    <select
                      id="reserva-duracao"
                      value={String(durationHours)}
                      onChange={e => setDurationHours(Number(e.target.value))}
                      className={input}
                    >
                      {Array.from({ length: maxDuration }, (_, i) => i + 1).map(h => (
                        <option key={h} value={String(h)}>{h}h</option>
                      ))}
                    </select>
                  </div>
                  {startTime && endTime && (
                    <p className={cn('col-span-2 text-xs', subtext)}>
                      Reserva das <span className="font-semibold">{startTime}</span> às <span className="font-semibold">{endTime}</span>
                    </p>
                  )}
                  {dateISO && startOptions.length === 0 && (
                    <p className={cn('col-span-2 flex items-center gap-1.5 text-xs font-medium', lightTheme ? 'text-gray-700' : 'text-gray-200')}>
                      <span className="h-1.5 w-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                      Não há horário livre nesta data para {durationHours}h.
                    </p>
                  )}
                </div>
              )}

              {/* What the day already holds — so the operator can tell the resident outright. */}
              {dateISO && busy.length > 0 && (
                <div className={cn('rounded-lg border px-3 py-2', lightTheme ? 'bg-gray-50 border-gray-200' : 'bg-gray-800/50 border-gray-700')}>
                  <p className={cn('text-[11px] font-semibold uppercase tracking-wide mb-1', subtext)}>Já reservado neste dia</p>
                  <ul className={cn('text-xs space-y-0.5', text)}>
                    {busy.map(b => <li key={`${b.start}-${b.end}`}>{b.start} – {b.end}</li>)}
                  </ul>
                </div>
              )}

              <div>
                {/* Not required: the portaria is booking for a resident standing at the counter,
                    and making them invent a reason to get past the form is friction for nothing.
                    The server has never required it either. */}
                <label htmlFor="reserva-finalidade" className={label}>Finalidade</label>
                <input
                  id="reserva-finalidade"
                  type="text"
                  value={purpose}
                  onChange={e => setPurpose(e.target.value)}
                  placeholder="Ex: Aniversário, Reunião de família (opcional)"
                  className={input}
                />
              </div>

              <div>
                <label htmlFor="reserva-obs" className={label}>Observações</label>
                <textarea
                  id="reserva-obs"
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  rows={2}
                  placeholder="Observações adicionais (opcional)..."
                  className={input}
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className={cn('flex justify-end gap-3 px-6 py-4 border-t', divider)}>
            <button
              type="button"
              onClick={onClose}
              className={cn('px-4 py-2 text-sm font-medium rounded-lg border',
                lightTheme ? 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50' : 'bg-gray-800 border-gray-700 text-gray-200 hover:bg-gray-700')}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {submitting ? 'Enviando...' : 'Reservar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NovaReservaModal;
